import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { maskDecimal } from '@givebutter/util';
import { updateRewardStatus as updateRewardStatusApi } from 'api/onboarding';
import { useOnboardingTasks } from 'components/onboarding-checklist/useOnboardingTasks';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Campaign } from 'types/domain';

const useChecklist = () => {
    const [ctaAck, setCtaAck] = useState(false);
    const [showMaxRaisedModal, setShowMaxRaisedModal] = useState(false);
    const { isCompletelyVerified, firstName } = useCurrentUser();
    const { tasks } = useOnboardingTasks();
    const { mutate: updateRewardStatus } = useMutation({ mutationFn: updateRewardStatusApi });
    const { accountId } = useCurrentAccount();
    const currencySymbol = useCurrencySymbol();
    const percentComplete = getCompletionPercentage([
        !!tasks?.hasCampaign,
        !!tasks?.hasBank,
        !!tasks?.hasRaisedFirstDollar,
        isCompletelyVerified,
    ]);
    const accountChecklistText = 'Verify email and phone';
    const isVerifyBankCompleted = !!tasks?.hasBank;
    const jumboTitle = getTitle(
        firstName,
        !!tasks?.hasRaisedFirstDollar,
        isVerifyBankCompleted,
        tasks?.firstCampaign,
    );
    const jumboBody = getBody(
        !!tasks?.hasRaisedFirstDollar,
        isVerifyBankCompleted,
        tasks?.firstCampaign,
    );

    const { stickers: showStickers = false } = useFlags();

    useEffect(() => {
        if (!ctaAck && tasks?.canUseReward && !tasks?.didAckCta && showStickers) {
            setCtaAck(true);
            setShowMaxRaisedModal(true);
            updateRewardStatus({ accountId, status: 'clicked' });
        }
    }, [
        ctaAck,
        tasks?.hasRaisedOverMax,
        updateRewardStatus,
        tasks?.didAckCta,
        tasks?.canUseReward,
        accountId,
        showStickers,
    ]);

    return {
        firstCampaign: tasks?.firstCampaign,
        percentageOutOfMax: tasks?.percentageOutOfMax,
        percentComplete,
        //
        ctaAck,
        setCtaAck,
        //
        showMaxRaisedModal,
        setShowMaxRaisedModal,
        //
        jumboTitle,
        jumboBody,
        showJumboParty: !!tasks?.hasRaisedFirstDollar,
        //
        showGoalbar: !!tasks?.hasRaisedFirstDollar,
        goalbarAmount: currencySymbol + maskDecimal(tasks?.raised ?? ''),
        //
        showCampaignSubHeading: !!tasks?.hasRaisedFirstDollar,
        //
        showVerifyAccountChecklistItem: !isCompletelyVerified || !tasks?.hasCampaign,
        isVerifyAccountDisabled: isCompletelyVerified,
        isVerifyAccountCompleted: isCompletelyVerified,
        accountChecklistText,
        //
        showVerifyBankChecklistItem: !tasks?.hasBank || !tasks?.hasCampaign,
        isVerifyBankDisabled: tasks?.hasBank,
        isVerifyBankCompleted,
        //
        showVerifyCampaignChecklistItem: !tasks?.hasCampaign,
        isVerifyCampaignDisabled: tasks?.hasCampaign,
        isVerifyCampaignCompleted: !!tasks?.hasCampaign,
        //
        showFirstDollarChecklistItem: !tasks?.hasRaisedFirstDollar || !tasks?.hasCampaign,
        isFirstDollarCompleted: !!tasks?.hasRaisedFirstDollar,
        //
        showChecklistGuides: !!tasks?.hasCampaign,
    };
};

const getTitle = (
    name: string,
    hasRaisedFirstDollar: boolean,
    isVerifyBankCompleted: boolean,
    firstCampaign?: Campaign | null,
) => {
    if (isVerifyBankCompleted && !firstCampaign) {
        return 'Almost there!';
    }

    if (hasRaisedFirstDollar) {
        return `Woah! You're fundraising!`;
    }

    if (!!firstCampaign) {
        return `Your campaign is live!`;
    }

    return `Welcome ${name}!`;
};

const getBody = (
    hasRaisedFirstDollar: boolean,
    isVerifyBankCompleted: boolean,
    firstCampaign?: Campaign | null,
) => {
    if (isVerifyBankCompleted && !firstCampaign) {
        return 'Start a campaign to begin fundraising!';
    }

    if (hasRaisedFirstDollar) {
        return "Raise $1,000 and we'll send you a buttery gift!";
    }

    if (!!firstCampaign) {
        return 'Spread the word to potential donors and start raising money!';
    }

    return `Let's get you set up using Givebutter!`;
};

const getCompletionPercentage = (tasks: boolean[]) => {
    const uncompletedTasks = tasks.filter(task => task);

    return (uncompletedTasks.length / tasks.length) * 100;
};

export { useChecklist };
